import { Box, Button, FormHelperText } from '@mui/material';
import { useEffect, useState } from 'react';
import { BaseDialog } from '../../../../base/components/BaseDialog';
import { Flex } from '../../../../base/components/Flex';
import { CreateSessionPlaylistItemParamInput } from '../../../../graphql/graphql-operations';
import { useTranslationPrefix } from '../../../../hooks/useTranslationPrefix';
import { compareByOrdinalAndProp } from '../../../../lib/compare';
import PlaylistDialogParam from '../../../components/PlaylistBuilder/PlaylistDialogParam';
import { QrScene } from '../LaunchCompanyQrPage';

const getRequiredParams = (scene?: QrScene) => {
  //required, more then one option to select and not lang (lang is by user language)
  const requiredParams = scene?.scene.parameters.filter(
    (p) => p.required && p.values?.length > 1 && p.name !== 'lang',
  );
  return requiredParams || [];
};
export const validateRequiredQrSceneParams = (
  scene: QrScene,
  params: CreateSessionPlaylistItemParamInput[],
): boolean => {
  const requiredParams = scene.scene.parameters.filter(
    (p) => p.required && p.values?.length > 1,
  );
  if (!requiredParams.length) {
    //nothing required
    return true;
  }

  for (const required of requiredParams) {
    const param = params.find((p) => p.name === required.name && p.value);
    if (!param) {
      return false;
    }
  }

  return true;
};

interface Props {
  scene?: QrScene;
  open: boolean;

  onConfirm: (params: CreateSessionPlaylistItemParamInput[]) => void;
  onClose: () => void;
}

const QrAdditionalParamsDialog = ({
  scene,
  open,
  onConfirm,
  onClose,
}: Props) => {
  const { _t, t } = useTranslationPrefix(
    'LaunchPlaylistQrPageNew.paramsDialog',
  );
  const requiredParams = getRequiredParams(scene);

  const [selectedParams, setSelectedParams] = useState<Record<string, string>>(
    {},
  );
  const [missingParams, setMissingParams] = useState<Record<string, boolean>>(
    {},
  );

  useEffect(() => {
    if (open) {
      setSelectedParams({});
      setMissingParams({});
    }
  }, [open]);

  const handleChange = (paramId: string) => (valueId?: string) => {
    if (valueId) {
      setSelectedParams((params) => ({ ...params, [paramId]: valueId }));
      setMissingParams((missing) => ({ ...missing, [paramId]: false }));
    }
  };

  const handleConfirm = () => {
    const filledIds = Object.keys(selectedParams);
    const missing = requiredParams
      .map((d) => d.id)
      .filter((id) => !filledIds.includes(id));
    if (missing.length === 0) {
      const params: CreateSessionPlaylistItemParamInput[] = [];
      for (const paramId in selectedParams) {
        if (Object.prototype.hasOwnProperty.call(selectedParams, paramId)) {
          const valueId = selectedParams[paramId];
          const param = requiredParams.find((p) => p.id === paramId);
          const value = param?.values.find((v) => v.id === valueId);

          if (param && value) {
            params.push({
              paramId: param.id,
              name: param.name,
              valueId: value.id,
              value: value.name,
            });
          }
        }
      }
      onConfirm(params);
    } else {
      setMissingParams(
        missing.reduce(
          (result, paramId) => ({ ...result, [paramId]: true }),
          {},
        ),
      );
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={_t('title', 'Required parameters')}
      disableRestoreFocus
      PaperProps={{
        sx: {
          maxWidth: { md: '80vw', xs: '95vw' },
        },
      }}
    >
      <Flex flexDirection="column" marginTop={-2} px={4} gap={3}>
        {requiredParams.sort(compareByOrdinalAndProp('name_t')).map((param) => (
          <Box key={param.id} position="relative">
            <PlaylistDialogParam
              param={{ ...param, values: [...(param.values || [])] }}
              required
              enabledIds={param.values.map((p) => p.id)}
              selectedId={selectedParams[param.id]}
              onChange={handleChange(param.id)}
            />
            {missingParams[param.id] && (
              <FormHelperText error sx={{ textAlign: 'center', pt: 0.5 }}>
                {_t('required', 'Field is required')}
              </FormHelperText>
            )}
          </Box>
        ))}
        <Flex justifyContent="space-around" pt={5} gap={2}>
          <Button
            id="playlist-additional-params-cancel"
            color="primary"
            onClick={onClose}
            variant="outlined"
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            id="playlist-additional-params-confirm"
            color="primary"
            onClick={handleConfirm}
          >
            {_t('confirm', 'Confirm')}
          </Button>
        </Flex>
      </Flex>
    </BaseDialog>
  );
};

export default QrAdditionalParamsDialog;
